import { type ReactNode, type RefAttributes, forwardRef } from 'react';

import { type AvatarImageProps } from '@radix-ui/react-avatar';

import { useAuthenticatedImageApi } from '../../contexts';
import { useAuthenticatedImageUrl } from '../../hooks';
import { Container, Fallback, Image, NoImage } from './styles';

export interface ILazyImageProps
  extends AvatarImageProps, RefAttributes<HTMLImageElement> {
  alt?: string;
  children?: ReactNode;
  dataCy?: string;
  delayMs?: number;
  useCrossOrigin?: boolean;
}

export const LazyImage = forwardRef<HTMLImageElement, ILazyImageProps>(
  // eslint-disable-next-line no-restricted-syntax
  function LazyImage(
    {
      alt,
      children,
      dataCy,
      delayMs = 500,
      src,
      useCrossOrigin = true,
      ...props
    },
    ref
  ): JSX.Element {
    const api = useAuthenticatedImageApi();
    const { blobUrl } = useAuthenticatedImageUrl(src, api);
    const resolvedSrc = blobUrl ?? src;

    return (
      <Container>
        <Image
          {...props}
          ref={ref}
          src={resolvedSrc}
          alt={alt}
          data-cy={dataCy}
          {...(useCrossOrigin && { crossOrigin: 'use-credentials' })}
        />
        <Fallback data-cy="image-notFound" delayMs={delayMs}>
          {children ?? <NoImage data-cy={dataCy} />}
        </Fallback>
      </Container>
    );
  }
);
